import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { UrlObject } from 'url';

import type { ProductTypeOptions } from '@/types/product';
import { getCheckoutLineUnitPriceWithoutDiscount } from '@/utils/checkout';

import { AddStrapButton } from '@/components/AddStrapButton/AddStrapButton';
import { Complimentary } from '@/components/Complimentary/Complimentary';
import { EngravingModal } from '@/components/EngravingModal/EngravingModal';
import { Flex } from '@/components/Flex';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Loader } from '@/components/Loader';
import { ProductLineLink } from '@/components/ProductLineLink';
import { SalesTaxBadge } from '@/components/SalesTaxBadge';
import { CustomErrors } from '@/components/ServerErrors/ServerErrors';
import { Typography } from '@/components/Typography';
import { Visibility } from '@/components/Visibility';
import type { InputMaybe, Scalars } from '@/graphql/orderApp/api';
import type {
  CheckoutLineFragment,
  Product,
  TaxedMoneyFragment,
} from '@/graphql/saleor/api';
import { useIsEngravingActivated } from '@/hooks/useIsEngravingActivated';
import { getMultiselectValue } from '@/lib/attributes';
import { getLineSubheader } from '@/lib/checkout';
import { VOUCHER_TYPE, WARRANTY_EXTENSION_VARIANT_ID } from '@/lib/const';
import { getTranslatedName } from '@/lib/i18n';
import { checkIsProductLineFree } from '@/lib/order';
import { sumPrices } from '@/lib/prices';
import {
  findMediaByShotType,
  getImageScaleRatio,
  getProductPath,
  getProductType,
  getTranslatedProductName,
  isProductPersonalizedEngraving,
} from '@/lib/product';
import type { Maybe, ShoppingBagProductWithCheckoutLineId } from '@/lib/types';
import { AddAWarranty, commonMessages } from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { CheckoutState } from '@/states/checkout';
import { PreOrderInfo } from '@/views/ShoppingBagView/components/ShoppingBagCard/components';

import { StrapSelect } from '../StrapSelect';
import { WarrantyImage } from './components/WarrantyImage/WarrantyImage';
import { WatchProductAction } from './components/WatchProductAction/WatchProductAction';

import * as M from './messages';
import * as S from './ShoppingBagCard.styles';

export interface ShoppingBagCardProps extends Partial<
  Pick<CheckoutState, 'preOrderData'>
> {
  channel?: string;
  children?: ReactNode;
  customErrorMessage?: string;
  hasBraceletSizeNotSelectedError: boolean;
  hasEngraving?: boolean;
  hasExtendWarranty?: boolean;
  hasRelatedStrap: boolean;
  isDisabled?: boolean;
  isEagerLoading?: boolean;
  isProcessing?: boolean;
  isRelatedProduct?: boolean;
  lineId: string;
  onAddStrap?: (productVariant: ShoppingBagProductWithCheckoutLineId) => void;
  onDelete: (lineId: string) => void;
  onLineAddItem?: (
    id: string,
    cta: string,
    watchLineId?: InputMaybe<Scalars['ID']['input']>,
  ) => void;
  price: Maybe<TaxedMoneyFragment>;
  productVariant: ShoppingBagProductWithCheckoutLineId;
  relatedBuckle: Maybe<CheckoutLineFragment>;
  showBuckleSizeSelector: boolean;
  strapPack?: boolean;
  voucherType?: string;
  warrantyExtensionDuration?: string;
}

const renderNameBlock = (
  type: ProductTypeOptions,
  lineId: string,
  productName: string,
  localeCode: string,
  subheader?: string,
  href?: string | UrlObject,
  isWarranty?: boolean,
  warrantyExtensionDuration?: string,
) => {
  const nameContent = (
    <>
      <S.Name data-test-id={`ShoppingBagCardProductName-${lineId}`}>
        {isWarranty && warrantyExtensionDuration
          ? `${productName} (${warrantyExtensionDuration})`
          : productName}
      </S.Name>
      <S.SmallText data-test-id={`ShoppingBagCardSubheader-${lineId}`}>
        {subheader}
      </S.SmallText>
    </>
  );

  return (
    <ProductLineLink
      href={href}
      localeCode={localeCode}
      productName={productName}
      productType={type}
    >
      {nameContent}
    </ProductLineLink>
  );
};

export const ShoppingBagCard = memo(
  ({
    channel,
    children,
    customErrorMessage,
    hasBraceletSizeNotSelectedError,
    hasEngraving,
    hasExtendWarranty,
    hasRelatedStrap,
    isDisabled = false,
    isEagerLoading,
    isProcessing = false,
    isRelatedProduct = false,
    lineId,
    onAddStrap,
    onDelete,
    onLineAddItem,
    preOrderData,
    price,
    productVariant,
    relatedBuckle,
    showBuckleSizeSelector,
    strapPack = false,
    voucherType,
    warrantyExtensionDuration,
  }: ShoppingBagCardProps) => {
    const { warrantyExtensionCampaignEnable } = useConfigProvider();
    const { locale } = useRegionProvider();
    const isEngravingActivated = useIsEngravingActivated();
    const { formatMessage } = useIntl();
    const wrapperRef = useRef<null | HTMLElement>(null);
    const [isEngravingModalOpen, setIsEngravingModalOpen] = useState(false);
    const [engravingWatch, setEngravingWatch] =
      useState<ShoppingBagProductWithCheckoutLineId | null>(null);
    const [modalLineId, setModalLineId] = useState<string | undefined>(
      undefined,
    );

    // Memoized event handlers
    const handleDeleteProduct = useCallback(() => {
      onDelete(productVariant.checkoutLineId);
    }, [onDelete, productVariant.checkoutLineId]);

    const handleDeleteBuckle = useCallback(() => {
      if (relatedBuckle) {
        onDelete(relatedBuckle.id);
      }
    }, [onDelete, relatedBuckle]);

    const handleOpenEngravingModal = useCallback(() => {
      if (!isProcessing) {
        setEngravingWatch(productVariant);
        setModalLineId(lineId);
        setIsEngravingModalOpen(true);
      }
    }, [isProcessing, productVariant, lineId]);

    const handleCloseEngravingModal = useCallback(() => {
      setIsEngravingModalOpen(false);
    }, []);

    const handleAddWarranty = useCallback(() => {
      if (!isProcessing) {
        onLineAddItem?.(WARRANTY_EXTENSION_VARIANT_ID, 'ORDER', lineId);
      }
    }, [isProcessing, onLineAddItem, lineId]);

    const handleAddStrap = useCallback(() => {
      onAddStrap?.(productVariant);
    }, [onAddStrap, productVariant]);

    const { product } = productVariant;

    const productTypeName = getProductType(product.productType);
    const { compatibleStraps, media, productType, thumbnail } = product;
    const isWatch = getProductType(productType) === 'watch';
    const subheader = getLineSubheader(
      productVariant.product,
      productVariant.sku,
    );
    const hasCompatibleStraps = !!getMultiselectValue(compatibleStraps).length;
    const href = getProductPath(
      productVariant.product,
      false,
      productVariant.sku,
    );
    const relatedUnitPriceWithoutDiscount = relatedBuckle
      ? getCheckoutLineUnitPriceWithoutDiscount(relatedBuckle, voucherType)
      : undefined;
    const productPrice =
      sumPrices(price, relatedUnitPriceWithoutDiscount) ?? price;

    useEffect(() => {
      if (hasBraceletSizeNotSelectedError && wrapperRef.current) {
        wrapperRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, [hasBraceletSizeNotSelectedError]);

    const isProductFree = checkIsProductLineFree(
      voucherType,
      productPrice,
      channel,
    );

    const productName = getTranslatedProductName(product);
    const picture = (
      isDesktop: boolean,
      type: ProductTypeOptions,
      isEagerLoadingParam?: boolean,
    ) => {
      // Always prefer Soldier image (slider image) for shopping bag cards
      const soldierImage = findMediaByShotType(media ?? [], 'Soldier');
      const displayImage = soldierImage ?? thumbnail;

      const content = displayImage ? (
        <S.Picture
          $scaleRatio={getImageScaleRatio(product?.productType)}
          alt={displayImage.alt ?? ''}
          data-test-id={`ShoppingBagCardPicture-${lineId}`}
          fetchPriority={isEagerLoadingParam ? 'high' : 'auto'}
          height={isDesktop ? 132 : 112}
          loading={isEagerLoadingParam ? 'eager' : 'lazy'}
          priority={isEagerLoadingParam}
          sizes='100vw'
          src={displayImage.url}
          width={isDesktop ? 88 : 72}
        />
      ) : (
        <S.Placeholder
          data-test-id={`ShoppingBagCardPlaceholder-${lineId}`}
          isDesktop={isDesktop}
          productType={
            product?.productType.name.toLowerCase() as ProductTypeOptions
          }
        />
      );

      return (
        <S.PictureWrapper $isDesktop={isDesktop}>
          <ProductLineLink
            href={href}
            localeCode={locale.code}
            productName={productName}
            productType={type}
          >
            {content}
          </ProductLineLink>
        </S.PictureWrapper>
      );
    };

    const isWarranty = productTypeName === 'warranty';
    const isPersonalizedEngravingProduct = isProductPersonalizedEngraving(
      productVariant.product,
    );

    return (
      <>
        <S.Wrapper
          ref={wrapperRef}
          $hasExtendWarranty={!!hasExtendWarranty}
          $hasRelatedStrap={hasRelatedStrap}
          $isRelatedProduct={isRelatedProduct}
          $productType={productType.name.toLowerCase()}
          data-test-id={`ShoppingBagCardWrapper-${lineId}`}
        >
          <S.CardAndButtonsWrapper>
            <S.Card
              $hasCompatibleStraps={hasCompatibleStraps}
              $hasRelatedStrap={hasRelatedStrap}
              data-test-id={`ShoppingBagCardCard-${lineId}`}
            >
              <Flex
                flexDirection='row'
                gridGap='s100'
                justifyContent='flex-start'
              >
                <>
                  {isWarranty && warrantyExtensionDuration ? (
                    <WarrantyImage
                      data-test-id={`ShoppingBagCardWarrantyImage-${lineId}`}
                      height='96px'
                      warrantyExtensionDuration={warrantyExtensionDuration}
                      width='96px'
                    />
                  ) : (
                    picture(true, productTypeName, isEagerLoading)
                  )}
                </>
                <Flex flexDirection='column' width='100%'>
                  <S.MiddleWrapper
                    data-test-id={`ShoppingBagCardMiddleWrapper-${lineId}`}
                  >
                    <div>
                      {renderNameBlock(
                        productTypeName,
                        lineId,
                        productName ?? '',
                        locale.code,
                        subheader,
                        href || '#',
                        isWarranty,
                        warrantyExtensionDuration ?? '',
                      )}
                      {isWatch && showBuckleSizeSelector && (
                        <StrapSelect
                          data-test-id={`ShoppingBagCardStrapSelect-${lineId}`}
                          hasError={hasBraceletSizeNotSelectedError}
                          line={product as Product}
                          lineId={lineId}
                        />
                      )}
                    </div>
                    <div>
                      {isProcessing && (
                        <Loader
                          data-testid={`removeProductLoader-${lineId}`}
                          height={35}
                          noMargin
                          size='sm'
                        />
                      )}
                      {!strapPack && (
                        <Visibility
                          handleDisplay={isProcessing}
                          xsHidden={isDisabled}
                        >
                          <S.DeleteMobile
                            aria-label={formatMessage(
                              commonMessages.removeFromShoppingBag,
                              { name: productName },
                            )}
                            data-test-id={`ShoppingBagCardDeleteMobile-${lineId}`}
                            data-testid='cartRemove'
                            onClick={handleDeleteProduct}
                            type='button'
                          >
                            <S.DeleteMobileIcon aria-hidden='true' />
                          </S.DeleteMobile>
                          <S.DeleteDesktop
                            ariaLabel={formatMessage(
                              commonMessages.removeFromShoppingBag,
                              { name: productName },
                            )}
                            data-test-id={`ShoppingBagCardDeleteDesktop-${lineId}`}
                            data-testid='cartRemove'
                            label={formatMessage(commonMessages.remove)}
                            onClick={handleDeleteProduct}
                            variant='link'
                          />
                        </Visibility>
                      )}
                    </div>
                  </S.MiddleWrapper>

                  {relatedBuckle && (
                    <S.BuckleWrapper
                      gridGap='s100'
                      justifyContent='space-between'
                    >
                      <Flex gridGap='s100'>
                        <S.BuckleImageWrapper>
                          {relatedBuckle.variant.product.thumbnail ? (
                            <S.Image
                              $scaleRatio={getImageScaleRatio(productType)}
                              alt={
                                relatedBuckle.variant.product.thumbnail?.alt ??
                                ''
                              }
                              fill
                              sizes='20vw'
                              src={
                                relatedBuckle.variant.product.thumbnail?.url ??
                                ''
                              }
                            />
                          ) : (
                            <ImagePlaceholder />
                          )}
                        </S.BuckleImageWrapper>
                        <Flex flexDirection='column'>
                          <S.BuckleName>
                            {getTranslatedName(
                              'name',
                              relatedBuckle.variant.product,
                            )}
                          </S.BuckleName>
                          <Typography color='secondaryGrey' scale='p3'>
                            {relatedBuckle.variant.sku}
                          </Typography>
                        </Flex>
                      </Flex>
                      <div>
                        {isProcessing && (
                          <Loader
                            data-testid={`removeRelatedBuckleLoader-${lineId}`}
                            height={35}
                            noMargin
                            size='sm'
                          />
                        )}
                        <Visibility
                          handleDisplay={isProcessing}
                          xsHidden={isDisabled}
                        >
                          <S.DeleteMobile
                            aria-label={formatMessage(
                              commonMessages.removeFromShoppingBag,
                              {
                                name: getTranslatedName(
                                  'name',
                                  relatedBuckle.variant.product,
                                ),
                              },
                            )}
                            data-testid='cartRemove'
                            onClick={handleDeleteBuckle}
                            type='button'
                          >
                            <S.DeleteMobileIcon aria-hidden='true' />
                          </S.DeleteMobile>
                          <S.DeleteDesktop
                            ariaLabel={formatMessage(
                              commonMessages.removeFromShoppingBag,
                              {
                                name: getTranslatedName(
                                  'name',
                                  relatedBuckle.variant.product,
                                ),
                              },
                            )}
                            data-testid='cartRemove'
                            label={formatMessage(commonMessages.remove)}
                            onClick={handleDeleteBuckle}
                            size='s'
                            variant='link'
                          />
                        </Visibility>
                      </div>
                    </S.BuckleWrapper>
                  )}

                  <S.EndWrapper>
                    <Flex alignItems='flex-end' flexDirection='column'>
                      {getProductType(productType) === 'gift' ? (
                        <Complimentary />
                      ) : (
                        <>
                          <S.Price
                            channel={channel}
                            data-test-id={`ShoppingBagCardPrice-${lineId}`}
                            displayZero={
                              voucherType !== VOUCHER_TYPE.PERCENTAGE
                            }
                            price={productPrice}
                          />
                          {!isProductFree && (
                            <SalesTaxBadge
                              channel={channel}
                              data-test-id={`ShoppingBagCardSalesTaxBadge-${lineId}`}
                            />
                          )}
                        </>
                      )}
                    </Flex>
                  </S.EndWrapper>
                </Flex>
              </Flex>

              {preOrderData && (
                <PreOrderInfo
                  data-test-id={`ShoppingBagCardPreOrderInfo-${lineId}`}
                  preOrderData={preOrderData}
                  price={price}
                />
              )}
              {preOrderData && (
                <S.PreOrderNote
                  data-test-id={`ShoppingBagCardPreOrderNote-${lineId}`}
                >
                  {M.PreOrderNote}
                </S.PreOrderNote>
              )}

              {children}
              {!preOrderData && isWatch && (
                <S.Buttons>
                  {warrantyExtensionDuration &&
                    !hasExtendWarranty &&
                    !warrantyExtensionCampaignEnable && (
                      <WatchProductAction
                        buttonText={AddAWarranty(warrantyExtensionDuration)}
                        data-test-id={`ShoppingBagCardAddWarranty-${lineId}`}
                        onClick={handleAddWarranty}
                      />
                    )}
                  {isEngravingActivated &&
                    isPersonalizedEngravingProduct &&
                    !hasEngraving && (
                      <WatchProductAction
                        buttonText={formatMessage(commonMessages.addEngraving)}
                        data-test-id={`ShoppingBagCardEngraving-${lineId}`}
                        onClick={handleOpenEngravingModal}
                      />
                    )}
                  {hasCompatibleStraps && (
                    <AddStrapButton
                      data-test-id='ShoppingBagItemAddAStrap'
                      onClick={handleAddStrap}
                      productVariant={productVariant}
                    />
                  )}
                </S.Buttons>
              )}
            </S.Card>
          </S.CardAndButtonsWrapper>
          <EngravingModal
            isOpen={isEngravingModalOpen}
            lineId={modalLineId}
            onClose={handleCloseEngravingModal}
            product={engravingWatch?.product}
          />
        </S.Wrapper>
        {customErrorMessage && (
          <CustomErrors message={customErrorMessage}></CustomErrors>
        )}
      </>
    );
  },
);

ShoppingBagCard.displayName = 'ShoppingBagCard';
